import { Progress } from '@/components/ui/progress'
import { categoryLabel, SITE_HEALTH_CATEGORIES } from '@/lib/siteHealth'
import { CARD_CLASS } from './SiteHealthStyles'
import type { SiteHealthCoverageProps } from './siteHealthComponents.types'

function UnavailableCoverageCard({ label }: { label: string }) {
  return (
    <article className="rounded-xl border border-border/70 bg-background p-4">
      <p className="text-sm font-medium">{label} unavailable</p>
      <p className="mt-3 text-sm text-muted-foreground">
        Coverage was not recorded for this audit.
      </p>
    </article>
  )
}

export default function SiteHealthCoverage({
  overview,
}: SiteHealthCoverageProps) {
  const eligibility = overview.eligibility
  const completeness = overview.completeness
  const notEvaluatedCategories = SITE_HEALTH_CATEGORIES.filter((category) => {
    const score = (overview.categoryScores ?? []).find(
      (candidate) => candidate.category === category
    )

    return (
      !score ||
      score.evaluationState === 'not_evaluated' ||
      score.evaluatedRules === 0
    )
  })

  return (
    <section className={`${CARD_CLASS} p-6`} aria-labelledby="coverage-title">
      <h2 id="coverage-title" className="text-lg font-semibold">
        Coverage overview
      </h2>

      <div className="mt-5 grid gap-4 md:grid-cols-3">
        {eligibility ? (
          <article className="rounded-xl border border-border/70 bg-background p-4">
            <p className="text-sm font-medium">Pages we could assess</p>
            <p className="mt-3 text-2xl font-semibold tabular-nums">
              {eligibility.eligiblePages} /{' '}
              {eligibility.customerObservableAttempts}
            </p>
            <Progress
              aria-label="Pages we could assess"
              className="mt-3 h-1.5"
              value={eligibility.percent}
            />
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {eligibility.eligiblePages ===
                eligibility.customerObservableAttempts &&
              eligibility.customerObservableAttempts > 0 ? (
                <>
                  We could assess all {eligibility.eligiblePages} pages
                  requested from your site.
                </>
              ) : (
                <>
                  We could assess {eligibility.eligiblePages} of{' '}
                  {eligibility.customerObservableAttempts} pages requested from
                  your site. This limits the highest possible score to{' '}
                  {eligibility.percent}%.
                </>
              )}
            </p>
          </article>
        ) : (
          <UnavailableCoverageCard label="Pages we could assess" />
        )}

        {completeness ? (
          <article className="rounded-xl border border-border/70 bg-background p-4">
            <p className="text-sm font-medium">Audit coverage</p>
            <p className="mt-3 text-2xl font-semibold tabular-nums">
              {completeness.auditedPages} / {completeness.discoveredPages}
            </p>
            <Progress
              aria-label="Audit coverage"
              className="mt-3 h-1.5"
              max={completeness.discoveredPages || 1}
              value={completeness.auditedPages}
            />
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {completeness.auditedPages === completeness.discoveredPages &&
              completeness.discoveredPages > 0 ? (
                <>
                  We audited all {completeness.auditedPages} pages found on your
                  site.
                </>
              ) : (
                <>
                  We audited {completeness.auditedPages} of the{' '}
                  {completeness.discoveredPages} pages found on your site.
                </>
              )}
            </p>
            {completeness.failedPlatformAttempts > 0 && (
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                We could not audit {completeness.failedPlatformAttempts}{' '}
                {completeness.failedPlatformAttempts === 1 ? 'page' : 'pages'}{' '}
                because of a temporary system error.
              </p>
            )}
          </article>
        ) : (
          <UnavailableCoverageCard label="Audit coverage" />
        )}

        {completeness ? (
          <article className="rounded-xl border border-border/70 bg-background p-4">
            <p className="text-sm font-medium">Rendered pages</p>
            <p className="mt-3 text-2xl font-semibold tabular-nums">
              {completeness.renderedPages} / {completeness.auditedPages}
            </p>
            <Progress
              aria-label="Rendered pages"
              className="mt-3 h-1.5"
              max={completeness.auditedPages || 1}
              value={completeness.renderedPages}
            />
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              We fully rendered {completeness.renderedPages} of the{' '}
              {completeness.auditedPages} pages we audited, including content
              loaded by scripts.
            </p>
          </article>
        ) : (
          <UnavailableCoverageCard label="Rendered pages" />
        )}
      </div>

      {notEvaluatedCategories.length > 0 && (
        <div className="mt-5 border-t border-border/70 pt-4">
          <p className="text-sm font-medium">Not evaluated</p>
          <ul
            className="mt-2 flex flex-wrap gap-2"
            aria-label="Not evaluated categories"
          >
            {notEvaluatedCategories.map((category) => (
              <li
                key={category}
                className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs text-muted-foreground"
              >
                {categoryLabel(category)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
